import { useEffect, useId, useRef } from "react";
import type { HTMLAttributes, MouseEvent, ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "../lib/cn";

export type DrawerSide = "right" | "bottom" | "left" | "responsive";

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDialogElement>, "title"> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  /**
   * `"responsive"` (padrão) = direita no desktop, de baixo no mobile —
   * que é a regra da seção 11 para filtros, date picker e ações em lote.
   */
  side?: DrawerSide;
  /** Largura no desktop. Padrão 480px (`md:w-120`). */
  widthClassName?: string;
  showCloseButton?: boolean;
  closeLabel?: string;
}

/**
 * Painel lateral sobre `<dialog>` nativo — mesmo motivo do Dialog:
 * foco preso, ESC e devolução de foco saem de graça no navegador.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  footer,
  side = "responsive",
  widthClassName = "md:w-120",
  showCloseButton = true,
  closeLabel = "Fechar",
  className,
  children,
  ...props
}: DrawerProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-description`;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const handleClose = () => onOpenChange(false);
    node.addEventListener("close", handleClose);
    return () => node.removeEventListener("close", handleClose);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === ref.current) onOpenChange(false);
  };

  // Painel FLUTUANTE: 12px de folga das bordas e raio 20 inteiro (não encosta na
  // janela). De baixo no mobile continua colado na base, como folha.
  const position =
    side === "right"
      ? "inset-y-3 right-3 left-auto h-[calc(100dvh_-_1.5rem)] w-[calc(100%_-_1.5rem)] max-w-full rounded-xl open:animate-drawer-right-in"
      : side === "left"
        ? "inset-y-3 left-3 right-auto h-[calc(100dvh_-_1.5rem)] w-[calc(100%_-_1.5rem)] max-w-full rounded-xl open:animate-drawer-left-in"
        : side === "bottom"
          ? "inset-x-0 bottom-0 top-auto max-h-[85dvh] w-full rounded-t-xl open:animate-drawer-bottom-in"
          : // responsive: de baixo no mobile, flutuando à direita no md+
            [
              "inset-x-0 bottom-0 top-auto max-h-[85dvh] w-full rounded-t-xl",
              "open:animate-drawer-bottom-in",
              "md:inset-y-3 md:right-3 md:left-auto md:bottom-3 md:h-[calc(100dvh_-_1.5rem)] md:max-h-none",
              "md:rounded-xl md:open:animate-drawer-right-in",
            ].join(" ");

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClick={onBackdropClick}
      className={cn(
        "fixed m-0 p-0 bg-surface-raised text-foreground shadow-lg",
        position,
        side !== "bottom" && widthClassName,
        className,
      )}
      {...props}
    >
      {open ? (
        <div className="flex h-full max-h-[85dvh] flex-col md:max-h-none">
          {/* alça de arrasto visual no mobile — sinaliza "isso sobe de baixo" */}
          <div
            aria-hidden="true"
            className="mx-auto mt-2 h-1 w-10 rounded-full bg-border-strong md:hidden"
          />
          <header className="flex items-start justify-between gap-3 border-b border-border px-5 pt-5 pb-4">
            <div className="min-w-0 flex flex-col gap-1">
              <h2 id={titleId} className="text-lg">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="text-sm text-muted-foreground">
                  {description}
                </p>
              ) : null}
            </div>
            {showCloseButton ? (
              <button
                type="button"
                aria-label={closeLabel}
                onClick={() => onOpenChange(false)}
                className="hit-44 -mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            ) : null}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-5 text-sm scrollbar-thin">
            {children}
          </div>

          {footer ? (
            <footer className="flex flex-col-reverse gap-2 border-t border-border px-5 pt-4 pb-[max(1rem,_env(safe-area-inset-bottom,_0px))] sm:flex-row sm:items-center sm:justify-end">
              {footer}
            </footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
